import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import createAuthRefreshInterceptor, { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh'
import { TokenResponse } from '../types/tokenResponse'
import {
  getRefreshKey,
  getTokenKey,
  setRefreshKey,
  setTokenKey,
  setUserName,
  setLastSelectedMachine,
} from './localStorageUtils'

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
})

const axiosAnonymousInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
})

axiosInstance.interceptors.request.use((request: AxiosRequestConfig): AxiosRequestConfig => {
  request.headers = {
    ...request.headers,
    Authorization: `Bearer ${getTokenKey()}`,
  }
  return request
})

export const isLoggedIn = () => {
  return !!getTokenKey()
}

const client_id = process.env.REACT_APP_OAUTH_CLIENT_ID

type FailedRequest = any
const refreshAuthLogic = (failedRequest: FailedRequest): Promise<FailedRequest> =>
  axiosInstance
    .post(
      `/o/token/`,
      {
        grant_type: 'refresh_token',
        refresh_token: getRefreshKey(),
        client_id: client_id,
      },
      {
        method: 'post',
      }
    )
    .then((tokenRefreshResponse: { data: TokenResponse }) => {
      setTokenKey(tokenRefreshResponse.data.access_token)
      setRefreshKey(tokenRefreshResponse.data.refresh_token)
      failedRequest.response.config.headers['Authorization'] = 'Bearer ' + tokenRefreshResponse.data.access_token
      return Promise.resolve()
    })
    .catch(() => {
      logoutUser()
    })

// Instantiate the interceptor
createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic)

export const get = <Response>(url: string): Promise<{ data: Response }> => {
  return axiosInstance.get(url, {
    headers: {
      Accept: 'application/json, text/javascript, */*; q=0.01',
    },
  })
}

export const post = <Payload, Response>(url: string, data: Payload): Promise<{ data: Response }> => {
  return axiosInstance.post(url, data, {
    headers: {
      Accept: 'application/json, text/javascript, */*; q=0.01',
    },
  })
}

export const put = <Payload, Response>(url: string, data: Payload): Promise<{ data: Response }> => {
  return axiosInstance.put(url, data, {
    headers: {
      Accept: 'application/json, text/javascript, */*; q=0.01',
    },
  })
}

export const patch = <Payload, Response>(url: string, data: Payload): Promise<{ data: Response }> => {
  return axiosInstance.patch(url, data, {
    headers: {
      Accept: 'application/json, text/javascript, */*; q=0.01',
    },
  })
}

// delete is a reserved word in javascript, use remove instead
export const remove = <Response>(url: string): Promise<{ data: Response }> => {
  return axiosInstance.delete(url, {
    headers: {
      Accept: 'application/json, text/javascript, */*; q=0.01',
    },
  })
}

export const getAnonymous = <Response>(url: string): Promise<{ data: Response }> => {
  return axiosAnonymousInstance.get(url, {
    headers: {
      Accept: 'application/json, text/javascript, */*; q=0.01',
    },
  })
}

export const postAnonymous = <Payload, Response>(url: string, data: Payload): Promise<{ data: Response }> => {
  return axiosAnonymousInstance.post(url, data, {
    headers: {
      Accept: 'application/json, text/javascript, */*; q=0.01',
    },
  })
}

export const login = (
  username: string,
  password: string,
  loggedInCallback: () => void,
  errorCallback: (
    errorCode: number | undefined,
    invalidGrant: boolean,
    noResponse: boolean,
    baseUrl: string | undefined
  ) => void
): void => {
  axiosInstance
    .post(
      `/o/token/`,
      {
        grant_type: 'password',
        username: username,
        password: password,
        client_id: client_id,
      },
      {
        method: 'post',
        skipAuthRefresh: true, // do not try to auto-refresh session on login request
      } as AxiosAuthRefreshRequestConfig
    )
    .then((response: { data: TokenResponse }) => {
      setUserName(username)
      setTokenKey(response.data.access_token)
      setRefreshKey(response.data.refresh_token)
      loggedInCallback()
    })
    .catch((error: AxiosError<any, any>) => {
      const noResponse = !error.response && error.request
      const invalidGrant = error.response?.data.error === 'invalid_grant'
      errorCallback(error.response?.status, invalidGrant, noResponse, error.config?.baseURL)
    })
}

export const logoutUser = (): void => {
  setUserName('')
  setTokenKey('') // TODO: request to invalidate token
  setRefreshKey('')
  setLastSelectedMachine('')
  window.location.href = '/login'
}

export const setPageTitle = (titleFragment: string): void => {
  const wholeFragment = titleFragment ? `${titleFragment} - ` : ''
  document.title = `${wholeFragment}ROEST`
}

export const isAnonymousUrl = (pathname: string): boolean => {
  return (
    pathname.indexOf('/signup') > -1 ||
    pathname.indexOf('/login') > -1 ||
    pathname.indexOf('/cuppings/addscore/') > -1 ||
    pathname.indexOf('/profilelibrary') > -1
  )
}

export const generateAbsoluteUrl = (relativeUrl: string): string => {
  const a = document.createElement('a')
  a.href = relativeUrl
  return a.href
}

export const isOsxSafari = (): boolean => {
  // https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browsers
  const isOsxSafari =
    // @ts-ignore: browser check
    /constructor/i.test(window.HTMLElement) ||
    (function (p) {
      return p.toString() === '[object SafariRemoteNotification]'
      // @ts-ignore: browser check
    })(!window['safari'] || (typeof safari !== 'undefined' && window['safari'].pushNotification))
  const isIpadOrIphone =
    !!navigator.userAgent.match(/Mac/) && !!navigator.maxTouchPoints && navigator.maxTouchPoints > 2

  return isOsxSafari || isIpadOrIphone
}
